import { AlertTriangle, Check } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { WizardEntry } from './draft';
import { stepRegistry } from './steps/registry';

export interface StepRailProps {
  entries: WizardEntry[];
  currentIndex: number;
  onJump: (index: number) => void;
}

function entryTitle(entry: WizardEntry): string {
  return entry.kind === 'frame' ? entry.title : entry.step.title;
}

function entryUnavailable(entry: WizardEntry): boolean {
  return entry.kind === 'template' && (entry.forceUnknown || !stepRegistry.has(entry.step.type));
}

/**
 * Wizard step rail (design §7.2/§22): an `<ol>` — current gets
 * `aria-current="step"` + `bg-accent`; completed items are `<button>`s with a
 * ✓ prefix (revisitable, E1/G2); future items are non-focusable spans marked
 * `aria-disabled` (prevents skipping ahead of validation). Unregistered steps
 * show a muted "unavailable" glyph but stay navigable (§16 — users must see
 * what's coming).
 */
export function StepRail({ entries, currentIndex, onJump }: StepRailProps) {
  return (
    <nav aria-label="Wizard steps" className="hidden w-64 shrink-0 border-r pr-6 lg:block">
      <ol className="space-y-1">
        {entries.map((entry, index) => {
          const title = entryTitle(entry);
          const unavailable = entryUnavailable(entry);
          const isCurrent = index === currentIndex;
          const isCompleted = index < currentIndex;

          const titleBlock = (
            <span className="flex min-w-0 items-center gap-2">
              <span className="line-clamp-2">{title}</span>
              {unavailable ? (
                <AlertTriangle
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              ) : null}
            </span>
          );

          const itemClasses = cn(
            'flex min-h-9 w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm',
            isCurrent && 'bg-accent text-accent-foreground',
            isCompleted && 'text-foreground hover:bg-accent/50',
            !isCurrent && !isCompleted && 'text-muted-foreground',
          );

          return (
            <li key={entry.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {isCompleted ? (
                    <button type="button" className={itemClasses} onClick={() => onJump(index)}>
                      <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {titleBlock}
                    </button>
                  ) : isCurrent ? (
                    <div className={itemClasses} aria-current="step">
                      {titleBlock}
                    </div>
                  ) : (
                    <div className={itemClasses} aria-disabled="true">
                      {titleBlock}
                    </div>
                  )}
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{title}</TooltipContent>
              </Tooltip>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
